import React, { useState, useEffect } from 'react';
import InputMask from 'react-input-mask';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Typography,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import VisibilityIcon from '@mui/icons-material/Visibility';
import withAuthAdmin from '../../middleware/useWithAuthAdmin';
import UserHttpService from '../../api/UserHttpService';
import LoadingOverlay from '../../components/LoadingOverlay';
import AdminBaseLayout from '../../components/layouts/AdminBaseLayout';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploadDialog, setUploadDialog] = useState({ open: false, file: null, cpfs: '' });
  const [singleUserDialog, setSingleUserDialog] = useState({
    open: false,
    name: '',
    cpf: '',
    email: '',
    phone: '',
    platform: 'alloyal'
  });
  const [bankAccountDialog, setBankAccountDialog] = useState({
    open: false,
    bankAccount: null
  });

  const [viewUserDialog, setViewUserDialog] = useState({
    open: false,
    id: null,
    fullName: '',
    cpf: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    street: '',
    neighborhood: '',
    number: '',
    complement: '',
    zipCode: '',
    pixKey: '',
    insurance: '',
    active: true,
    birthDate: null
  });

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setPage(0);
      fetchUsers(1, rowsPerPage, search);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  useEffect(() => {
    fetchUsers(page + 1, rowsPerPage, search);
  }, [page, rowsPerPage]);

  const fetchUsers = async (pageNumber = 1, limit = rowsPerPage, searchTerm = '') => {
    try {
      setLoading(true);
      const response = await UserHttpService.getUsers(pageNumber, limit, searchTerm);
      setUsers(response.data);
      setTotalItems(response.pagination.total);
      setTotalPages(response.pagination.total_pages);
    } catch (err) {
      showMessage('Erro ao carregar os dados dos usuários', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const showMessage = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCloseDialog = () => {
    setUploadDialog({ open: false, file: null, cpfs: '' });
  };

  const handleOpenUploadDialog = () => {
    setUploadDialog({ ...uploadDialog, open: true });
  };

  const handleCloseSingleUserDialog = () => {
    setSingleUserDialog({
      open: false,
      name: '',
      cpf: '',
      email: '',
      phone: '',
      platform: 'alloyal'
    });
  };

  const handleOpenSingleUserDialog = () => {
    setSingleUserDialog(prev => ({ ...prev, open: true }));
  };

  const handleOpenBankAccountDialog = (bankAccount) => {
    setBankAccountDialog({ open: true, bankAccount });
  };

  const handleCloseBankAccountDialog = () => {
    setBankAccountDialog({ open: false, bankAccount: null });
  };

  const handleCloseViewUserDialog = () => {
    setViewUserDialog(prev => ({
      ...prev,
      open: false
    }));
  };

  const handleOpenViewUserDialog = async (userId) => {
    try {
      setLoading(true);
      const userData = await UserHttpService.findById(userId);
      setViewUserDialog({
        open: true,
        ...userData
      });
    } catch (error) {
      showMessage('Erro ao carregar dados do usuário', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    try {
      setLoading(true);
      await UserHttpService.updateUserData(viewUserDialog);
      showMessage('Usuário atualizado com sucesso!');
      handleCloseViewUserDialog();
      fetchUsers(page + 1, rowsPerPage, search);
    } catch (error) {
      showMessage('Erro ao atualizar usuário', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const downloadSampleCSV = () => {
    const csvContent = 'nome;cpf;email;celular;plataforma\nJoão Silva;37686468070;joao@email.com;11999999999;alloyal\nMaria Santos;12881077064;maria@email.com;11988888888;alloyal';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modelo_usuarios.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setUploadDialog({ ...uploadDialog, file });
    } else {
      showMessage('Por favor, selecione um arquivo CSV válido', 'error');
    }
  };

  const handleUpload = async () => {
    if (!uploadDialog.file) {
      showMessage('Por favor, selecione um arquivo CSV', 'error');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('csv_file', uploadDialog.file);
      await UserHttpService.uploadUsersCSV(formData);
      showMessage('Arquivo processado com sucesso!');
      handleCloseDialog();
      fetchUsers(page + 1, rowsPerPage, search);
    } catch (error) {
      showMessage(error.message || 'Erro ao processar arquivo', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSingleUserSubmit = async () => {
    const { name, cpf, email, phone, platform } = singleUserDialog;
    
    if (!name || !cpf || !email || !phone) {
      showMessage('Por favor, preencha todos os campos obrigatórios', 'error');
      return;
    }

    try {
      setLoading(true);
      // Remove masks before sending
      const unmaskedCpf = cpf.replace(/\D/g, '');
      const unmaskedPhone = phone.replace(/\D/g, '');
      
      const csvContent = `nome;cpf;email;celular;plataforma\n${name};${unmaskedCpf};${email};${unmaskedPhone};${platform}`;
      const file = new File([csvContent], 'single_user.csv', { type: 'text/csv' });
      
      const formData = new FormData();
      formData.append('csv_file', file);
      
      await UserHttpService.uploadUsersCSV(formData);
      showMessage('Usuário cadastrado com sucesso!');
      handleCloseSingleUserDialog();
      fetchUsers(page + 1, rowsPerPage, search);
    } catch (error) {
      showMessage(error.message || 'Erro ao cadastrar usuário', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminBaseLayout>
      <Box sx={{ width: '100%', p: 3, position: 'relative' }}>
        {loading && <LoadingOverlay open={loading} />}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5" component="h1">
            Gerenciamento de Usuários
          </Typography>
          <Box>
            <Tooltip title="Adicionar Usuário">
              <Button
                variant="contained"
                color="primary"
                startIcon={<GroupAddIcon />}
                onClick={handleOpenSingleUserDialog}
                sx={{ mr: 2 }}
                disabled={loading}
              >
                Adicionar Usuário
              </Button>
            </Tooltip>
            <Tooltip title="Adicionar Usuários em Massa">
              <Button
                variant="contained"
                color="primary"
                startIcon={<CloudUploadIcon />}
                onClick={handleOpenUploadDialog}
                sx={{ mr: 2 }}
                disabled={loading}
              >
                Adicionar Usuários em Massa
              </Button>
            </Tooltip>
            <Tooltip title="Baixar base de usuários">
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownloadIcon />}
                component="a"
                href={`${process.env.REACT_APP_API_BASE_URL}/report/user/csv`}
                target="_blank"
                rel="noopener noreferrer"
                disabled={loading}
              >
                Baixar base de usuários
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <TextField
          placeholder="Buscar por nome, email, cpf, celular ou plataforma"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>CPF</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Celular</TableCell>
                <TableCell>Plano</TableCell>
                <TableCell>Plataforma</TableCell>
                <TableCell>Data de Criação</TableCell>
                <TableCell>Dados Bancários</TableCell>
                <TableCell align="center">Editar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.fullName}</TableCell>
                  <TableCell>{user.cpf}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.phone}</TableCell>
                  <TableCell>{user.plan}</TableCell>
                  <TableCell>{user?.user_platform}</TableCell>
                  <TableCell>{formatDate(user.created_at)}</TableCell>
                  <TableCell>
                    {user.bank_account && (
                        <Tooltip title="Visualizar Dados Bancários">
                          <IconButton
                              onClick={() => handleOpenBankAccountDialog(user.bank_account)}
                              color="primary"
                          >
                            <AccountBalanceIcon />
                          </IconButton>
                        </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                      <Tooltip title="Visualizar/Editar usuário">
                        <IconButton
                            onClick={() => handleOpenViewUserDialog(user.id)}
                            color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>

        {/* Modal de Upload */}
        <Dialog open={uploadDialog.open} onClose={handleCloseDialog}>
          <DialogTitle sx={{ 
            bgcolor: '#FF6B00', 
            color: 'white',
            fontSize: '1.5rem',
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <GroupAddIcon sx={{ fontSize: '2rem' }} />
            Adicionar Usuários em Massa
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Faça o upload de um arquivo CSV contendo os dados dos usuários.
              <br />
              O arquivo deve conter as seguintes colunas: nome, cpf, email, celular
              <br />
            </DialogContentText>
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Exemplo de arquivo CSV válido:
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  p: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  mb: 2
                }}
              >
                nome;cpf;email;celular;plataforma<br />
                João Silva;12345678912;joao@email.com;11999999999;alloyal<br />
                Maria Santos;98765432198;maria@email.com;11988888888;alloyal
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
                disabled={loading}
                sx={{
                  backgroundColor: "#FF6B00",
                  '&:hover': {
                    backgroundColor: "#e65100",
                  }
                }}
              >
                Selecionar Arquivo
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  onChange={handleFileSelect}
                  disabled={loading}
                />
              </Button>
              {uploadDialog.file && (
                <Typography variant="body2">
                  {uploadDialog.file.name}
                </Typography>
              )}
            </Box>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadSampleCSV}
              sx={{ mt: 2 }}
              disabled={loading}
            >
              Baixar Modelo CSV
            </Button>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseDialog} 
              disabled={loading}
              variant="outlined"
              size="large"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleUpload} 
              variant="contained" 
              disabled={loading || !uploadDialog.file}
              size="large"
              sx={{
                backgroundColor: "#FF6B00",
                '&:hover': {
                  backgroundColor: "#e65100",
                },
                minWidth: '120px'
              }}
            >
              {loading ? 'Processando...' : 'Processar'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={singleUserDialog.open}
          onClose={handleCloseSingleUserDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Adicionar Novo Usuário</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Nome"
                fullWidth
                value={singleUserDialog.name}
                onChange={(e) => setSingleUserDialog(prev => ({ ...prev, name: e.target.value }))}
                margin="normal"
                required
              />
              <InputMask
                mask="999.999.999-99"
                value={singleUserDialog.cpf}
                onChange={(e) => setSingleUserDialog(prev => ({ ...prev, cpf: e.target.value }))}
              >
                {(inputProps) => (
                  <TextField
                    {...inputProps}
                    label="CPF"
                    fullWidth
                    margin="normal"
                    required
                  />
                )}
              </InputMask>
              <TextField
                label="Email"
                fullWidth
                type="email"
                value={singleUserDialog.email}
                onChange={(e) => setSingleUserDialog(prev => ({ ...prev, email: e.target.value }))}
                margin="normal"
                required
              />
              <InputMask
                mask="(99) 99999-9999"
                value={singleUserDialog.phone}
                onChange={(e) => setSingleUserDialog(prev => ({ ...prev, phone: e.target.value }))}
              >
                {(inputProps) => (
                  <TextField
                    {...inputProps}
                    label="Celular"
                    fullWidth
                    margin="normal"
                    required
                  />
                )}
              </InputMask>
              <TextField
                label="Plataforma"
                fullWidth
                value={singleUserDialog.platform}
                onChange={(e) => setSingleUserDialog(prev => ({ ...prev, platform: e.target.value }))}
                margin="normal"
                required
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseSingleUserDialog}>Cancelar</Button>
            <Button onClick={handleSingleUserSubmit} variant="contained" color="primary">
              Cadastrar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Dados Bancários */}
        <Dialog
          open={bankAccountDialog.open}
          onClose={handleCloseBankAccountDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            bgcolor: '#FF6B00', 
            color: 'white',
            fontSize: '1.5rem',
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <AccountBalanceIcon sx={{ fontSize: '2rem' }} />
            Dados Bancários
          </DialogTitle>
          <DialogContent>
            {bankAccountDialog.bankAccount && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Banco:</strong> {bankAccountDialog.bankAccount.bank}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Código:</strong> {bankAccountDialog.bankAccount.code}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Agência:</strong> {bankAccountDialog.bankAccount.agency}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Conta:</strong> {bankAccountDialog.bankAccount.account_number}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Chave PIX:</strong> {bankAccountDialog.bankAccount.pix}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseBankAccountDialog}
              variant="contained"
              size="large"
              sx={{
                backgroundColor: "#FF6B00",
                '&:hover': {
                  backgroundColor: "#e65100",
                },
                minWidth: '120px'
              }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Visualização/Edição de Usuário */}
        <Dialog
          open={viewUserDialog.open}
          onClose={handleCloseViewUserDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ 
            bgcolor: '#FF6B00', 
            color: 'white',
            fontSize: '1.5rem',
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <VisibilityIcon sx={{ fontSize: '2rem' }} />
            Visualizar/Editar Usuário
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Nome Completo"
                fullWidth
                value={viewUserDialog.fullName || ''}
                onChange={(e) => setViewUserDialog(prev => ({ ...prev, fullName: e.target.value }))}
                margin="normal"
              />
              <InputMask
                mask="999.999.999-99"
                value={viewUserDialog.cpf || ''}
                onChange={(e) => setViewUserDialog(prev => ({ ...prev, cpf: e.target.value }))}
              >
                {(inputProps) => (
                  <TextField
                    {...inputProps}
                    label="CPF"
                    fullWidth
                    margin="normal"
                  />
                )}
              </InputMask>
              <TextField
                label="Email"
                fullWidth
                type="email"
                value={viewUserDialog.email || ''}
                onChange={(e) => setViewUserDialog(prev => ({ ...prev, email: e.target.value }))}
                margin="normal"
              />
              <InputMask
                mask="(99) 99999-9999"
                value={viewUserDialog.phone || ''}
                onChange={(e) => setViewUserDialog(prev => ({ ...prev, phone: e.target.value }))}
              >
                {(inputProps) => (
                  <TextField
                    {...inputProps}
                    label="Celular"
                    fullWidth
                    margin="normal"
                  />
                )}
              </InputMask>
              <TextField
                label="CEP"
                fullWidth
                value={viewUserDialog.zipCode || ''}
                onChange={(e) => setViewUserDialog(prev => ({ ...prev, zipCode: e.target.value }))}
                margin="normal"
              />
              <TextField
                label="Cidade"
                fullWidth
                value={viewUserDialog.city || ''}
                onChange={(e) => setViewUserDialog(prev => ({ ...prev, city: e.target.value }))}
                margin="normal"
              />
              <TextField
                label="Estado"
                fullWidth
                value={viewUserDialog.state || ''}
                onChange={(e) => setViewUserDialog(prev => ({ ...prev, state: e.target.value }))}
                margin="normal"
              />
              <TextField
                label="Rua"
                fullWidth
                value={viewUserDialog.street || ''}
                onChange={(e) => setViewUserDialog(prev => ({ ...prev, street: e.target.value }))}
                margin="normal"
              />
              <TextField
                label="Bairro"
                fullWidth
                value={viewUserDialog.neighborhood || ''}
                onChange={(e) => setViewUserDialog(prev => ({ ...prev, neighborhood: e.target.value }))}
                margin="normal"
              />
              <TextField
                label="Número"
                fullWidth
                value={viewUserDialog.number || ''}
                onChange={(e) => setViewUserDialog(prev => ({ ...prev, number: e.target.value }))}
                margin="normal"
              />
              <TextField
                label="Complemento"
                fullWidth
                value={viewUserDialog.complement || ''}
                onChange={(e) => setViewUserDialog(prev => ({ ...prev, complement: e.target.value }))}
                margin="normal"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseViewUserDialog}>Cancelar</Button>
            <Button onClick={handleUpdateUser} variant="contained" color="primary">
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminBaseLayout>
  );
}

export default withAuthAdmin(AdminUsers);
